import React from "react";

import { Column } from "primereact/column";
import { PaginatorPageChangeEvent } from "primereact/paginator";

import {
  DEFAULT_TABLE_ROWS_PER_PAGE,
  DEFAULT_TABLE_ROWS_PER_PAGE_OPTIONS,
} from "../../../shared/ui/data-table/model/data-table-contract";
import type {
  IProblemRecord,
  ISection,
  ISubnavItem,
  ITableBlock,
  ITableColumn,
  ITableUI,
} from "../../../shared/ui/data-table/model/data-table-contract";
import {
  TablePaginationHeader,
  TablePaginatorFooter,
} from "../../../shared/ui/data-table/ui/table-paginator-footer";
import { renderTableSortIcon } from "../../../shared/ui/data-table/ui/table-sort-icon";
import * as Styled from "./single-data-table-lab-results.styled";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTES } from "../../../system/router/routes";

export type {
  IProblemRecord,
  ISection,
  ISubnavItem,
  ITableBlock,
  ITableColumn,
  ITableUI,
};

interface TablePaginationState {
  first: number;
  rows: number;
}

export const SingleDataTableLabResults = ({
  healthEntity,
}: {
  healthEntity: IProblemRecord;
}) => {
  const [tablePaginationState, setTablePaginationState] = React.useState<
    Record<string, TablePaginationState>
  >({});
  const navigate = useNavigate();
  const patientID = useParams().patientID;
  const encounterID = useParams().encounterID;

  if (!patientID || !encounterID) return null;
  // const isUrl = (value: unknown): value is string => {
  //   if (typeof value !== "string") return false;

  //   if (!value.includes(".") && !value.startsWith("http")) return false;

  //   try {
  //     const url = new URL(
  //       value.startsWith("http") ? value : `https://${value}`,
  //     );
  //     return url.protocol === "http:" || url.protocol === "https:";
  //   } catch {
  //     return false;
  //   }
  // };

  const getRowsPerPageOptions = (
    configuredRowsPerPage: number | undefined,
    configuredOptions: number[] | undefined,
  ) => {
    const baseOptions =
      configuredOptions && configuredOptions.length > 0
        ? configuredOptions
        : Array.from(DEFAULT_TABLE_ROWS_PER_PAGE_OPTIONS);

    if (
      configuredRowsPerPage === undefined ||
      baseOptions.includes(configuredRowsPerPage)
    ) {
      return baseOptions;
    }

    return [configuredRowsPerPage, ...baseOptions];
  };

  return (
    <>
      {healthEntity?.sections && healthEntity.sections.length > 0 && (
        <>
          {healthEntity.sections.map((section) => (
            <Styled.Entity key={section.id}>
              {healthEntity.sections.length > 0 && (
                <Styled.EntityTitle id={section.id}>
                  {section.title}
                </Styled.EntityTitle>
              )}
              {/* TABLES */}
              {section.tables.length > 0 ? (
                section.tables?.map((table) => {
                  const defaultRowsPerPage =
                    table.ui?.defaultRowsPerPage ??
                    DEFAULT_TABLE_ROWS_PER_PAGE;
                  const rowsPerPageOptions = getRowsPerPageOptions(
                    defaultRowsPerPage,
                    table.ui?.rowsPerPageOptions,
                  );
                  const paginationState = tablePaginationState[table.id];
                  const activeRowsPerPage =
                    paginationState?.rows ?? defaultRowsPerPage;
                  const maxFirst =
                    table.rows.length > 0
                      ? Math.floor((table.rows.length - 1) / activeRowsPerPage) *
                        activeRowsPerPage
                      : 0;
                  const activeFirst = Math.min(
                    paginationState?.first ?? 0,
                    maxFirst,
                  );
                  const visibleRows = table.rows.slice(
                    activeFirst,
                    activeFirst + activeRowsPerPage,
                  );

                  return (
                    <Styled.TableWrapper key={table.id}>
                      <TablePaginationHeader
                        first={activeFirst}
                        rows={activeRowsPerPage}
                        totalRecords={table.rows.length}
                        rowsPerPageOptions={rowsPerPageOptions}
                        onRowsChange={(rows) => {
                          setTablePaginationState((current) => ({
                            ...current,
                            [table.id]: {
                              first: 0,
                              rows,
                            },
                          }));
                        }}
                      />
                      <Styled.Table
                        value={visibleRows}
                        tableStyle={{ minWidth: "20rem" }}
                        removableSort
                        sortIcon={renderTableSortIcon}
                        emptyMessage="No records found."
                        onRowClick={(row) => {
                          if (row.data?.resultType === "labResult") {
                            console.log("Lab result clicked:");
                            navigate(
                              ROUTES.PATIENT_DETAILS_ENCOUNTERS_LABS_REPORT_DETAILS.replace(
                                ":patientID",
                                patientID,
                              )
                                .replace(":encounterID", encounterID)
                                .replace(":labResultID", row.data.id),
                            );
                          } else if (
                            row.data?.resultType === "procedureResult"
                          ) {
                            console.log("Procedure result clicked:");

                            navigate(
                              ROUTES.PATIENT_DETAILS_ENCOUNTERS_LABS_REPORT_PROCEDURE_DETAILS.replace(
                                ":patientID",
                                patientID,
                              )
                                .replace(":encounterID", encounterID)
                                .replace(":procedureResultID", row.data.id),
                            );
                          }
                        }}
                      >
                        {table.columns.map((c) => (
                          <Column
                            key={c.key}
                            field={c.key}
                            header={c.header}
                            sortable={!!c.sortable}
                            body={(row) => {
                              const v = row?.[c.key];

                              switch (v) {
                                case "labResult":
                                  return (
                                    <svg
                                      width="20"
                                      height="20"
                                      viewBox="0 0 20 20"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M12.4994 14.1674L16.666 10.0006L12.4993 5.83398"
                                        stroke="#7C7F7E"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                      <path
                                        d="M16.666 10H3.33268"
                                        stroke="#7C7F7E"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  );
                                case "procedureResult":
                                  return (
                                    <svg
                                      width="20"
                                      height="20"
                                      viewBox="0 0 20 20"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M12.4994 14.1674L16.666 10.0006L12.4993 5.83398"
                                        stroke="#7C7F7E"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                      <path
                                        d="M16.666 10H3.33268"
                                        stroke="#7C7F7E"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  );
                                default:
                                  return v ?? "—";
                              }
                            }}
                          />
                        ))}
                      </Styled.Table>
                      <TablePaginatorFooter
                        first={activeFirst}
                        rows={activeRowsPerPage}
                        totalRecords={table.rows.length}
                        rowsPerPageOptions={rowsPerPageOptions}
                        onPageChange={(event: PaginatorPageChangeEvent) => {
                          setTablePaginationState((current) => ({
                            ...current,
                            [table.id]: {
                              first: event.first,
                              rows: event.rows,
                            },
                          }));
                        }}
                      />
                    </Styled.TableWrapper>
                  );
                })
              ) : (
                <Styled.NoData>No data available</Styled.NoData>
              )}
            </Styled.Entity>
          ))}
        </>
      )}
    </>
  );
};
